/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow, MediumFormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import VacancyWysiwygInput from 'domain/recruiting/vacancy/shared/input/VacancyWysiwygInput';
import { EnumDropdown, useFormContext, TextInput, CountyOfResidencesEnumDropdown, COUNTRIES } from 'framework/form';
import { useSearchParameter } from 'framework/routing';
import useVacancyPublicationService, {
  VACANCY_PUBLICATION_PLATFORMS,
  VACANCY_PUBLICATION_STATUSES,
} from 'domain/recruiting/vacancy/publication/shared/vacancyPublicationService';
import MinimalAndBetterEnglishLevelEnumDropdown from 'domain/recruiting/vacancy/publication/shared/input/MinimalAndBetterEnglishLevelEnumDropdown';
import AdditionalVacancyQuestionList from './AdditionalVacancyQuestionList';
import AdditionalVacancyQuestion from './AdditionalVacancyQuestion';
import { Loading } from 'framework/ui/mui-builder';

interface VacancyPublicationFormProps {
  isCreateForm?: boolean;
}

const COUNTRY_OF_RESIDENCE = COUNTRIES.filter(country => country.name !== 'Unknown').reduce(
  (prev, curr) => ({ ...prev, [curr.name.toUpperCase()]: curr.name }),
  {},
);

const ALL_DROP_DOWN_COUNTRIES = {
  ALL: 'All',
  EU: 'EU',
  ...COUNTRY_OF_RESIDENCE,
};

const VacancyPublicationForm = ({ isCreateForm = false }: VacancyPublicationFormProps) => {
  const { onChangeCallback } = useFormContext();
  const vacancyAlias = useSearchParameter('vacancyAlias');
  const { constructVacancyPublicationDescription } = useVacancyPublicationService();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (onChangeCallback && vacancyAlias && isCreateForm) {
        const vacancyDescription = await constructVacancyPublicationDescription(vacancyAlias);
        onChangeCallback({ ...vacancyDescription }, true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    })();
  }, [vacancyAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormSection title="Vacancy Publication">
        <FormRow>
          <EnumDropdown
            source="type"
            label="Publication platform"
            options={VACANCY_PUBLICATION_PLATFORMS}
            placeholder="Choose platform"
          />
          <EnumDropdown
            source="status"
            label="Status"
            options={VACANCY_PUBLICATION_STATUSES}
            placeholder="Choose Status"
          />
        </FormRow>
        <FormRow>
          <MinimalAndBetterEnglishLevelEnumDropdown
            source="englishLevels"
            label="Relevant english levels"
            multiple
            placeholder="Choose minimal relevant english level"
            helpText="Choose minimal relevant english level and higher levels will be selected automatically.
            To remove the level - start from the lowest"
          />
          <CountyOfResidencesEnumDropdown
            source="countryOfResidences"
            label="Relevant country of residence"
            options={ALL_DROP_DOWN_COUNTRIES}
            multiple
            placeholder="Choose relevant country"
          />
        </FormRow>
        <BigFormRow>
          <AdditionalVacancyQuestionList
            source="formFields"
            inputTemplate={AdditionalVacancyQuestion}
            addText="Add question"
          />
        </BigFormRow>
        {isCreateForm && loading ? (
          <Loading />
        ) : (
          <>
            <MediumFormRow>
              <TextInput source="name" />
            </MediumFormRow>
            <VacancyWysiwygInput source="description" label="Public description" />
          </>
        )}
      </FormSection>
    </>
  );
};

export default VacancyPublicationForm;
